/**
 * Connect Slack page (Phase 3 Action 39).
 *
 * Two modes for completing the install:
 *
 *   1. Authorize now — operator clicks, the page navigates to Slack,
 *      Slack redirects back to /fleet/:id?installed=slack on success.
 *
 *   2. Copy link — operator sends the install URL to the customer's
 *      Slack admin via email/Slack/etc. While the operator waits,
 *      the page polls GET /:id/slack/connection every 5s and routes
 *      back to the tenant detail page once the connection lands.
 *
 * The install URL has a 10-min server-enforced TTL. If the operator
 * leaves the page open past that, the page shows a "Link expired"
 * state with a "Regenerate" button.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiPath } from '../api/baseUrl';
import { getStoredToken } from '../auth/AuthContext';

interface InstallUrlResponse {
  data: { installUrl: string; expiresAt: string };
}

interface ConnectionResponse {
  data: {
    connection: {
      workspaceId: string;
      workspaceName: string;
      botUserId: string;
      connectedAt: string;
      connectedByOperatorId: string | null;
    } | null;
  };
}

async function postInstallUrl(id: string): Promise<InstallUrlResponse> {
  const token = getStoredToken();
  const res = await fetch(apiPath(`/api/provisioning/tenants/${id}/slack/install-url`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    let message = `install-url failed: ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      if (body.message) message = body.message;
      if (res.status === 503) message = 'Slack OAuth is not configured on this deployment. Set SLACK_CLIENT_ID and SLACK_CLIENT_SECRET in Render.';
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return res.json();
}

async function getConnection(id: string): Promise<ConnectionResponse> {
  const token = getStoredToken();
  const res = await fetch(apiPath(`/api/provisioning/tenants/${id}/slack/connection`), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`connection fetch failed: ${res.status}`);
  return res.json();
}

export default function ConnectSlack() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();

  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Generate the install URL on mount.
  const urlMutation = useMutation({
    mutationFn: () => postInstallUrl(id),
    onSuccess: (res) => {
      setInstallUrl(res.data.installUrl);
      setExpiresAt(res.data.expiresAt);
      setCopied(false);
    },
  });

  useEffect(() => {
    if (id) urlMutation.mutate();
    // urlMutation reference is stable; rerun only on id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Tick the clock once per second so the "expires in N min" countdown
  // moves and the expired state flips when we cross the TTL.
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const expired = useMemo(() => {
    if (!expiresAt) return false;
    return Date.now() > new Date(expiresAt).getTime();
  }, [expiresAt, now]);

  // Poll the connection endpoint every 5s. Stops once a connection is
  // found OR the install URL expires (manual regenerate restarts it).
  const connectionQ = useQuery({
    queryKey: ['slack-connection-poll', id],
    queryFn: () => getConnection(id),
    enabled: Boolean(id) && Boolean(installUrl) && !expired,
    refetchInterval: (q) => {
      // Stop polling once we have a connection — we'll navigate away
      // in the effect below.
      if (q.state.data?.data.connection) return false;
      return 5000;
    },
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    const connection = connectionQ.data?.data.connection;
    if (connection) {
      queryClient.invalidateQueries({ queryKey: ['tenant-slack', id] });
      navigate(`/fleet/${id}?installed=slack`);
    }
  }, [connectionQ.data, id, navigate, queryClient]);

  const expiresInMs = expiresAt ? new Date(expiresAt).getTime() - now : 0;
  const expiresInMin = Math.max(0, Math.floor(expiresInMs / 60000));
  const expiresInSec = Math.max(0, Math.floor((expiresInMs % 60000) / 1000));

  async function handleCopy() {
    if (!installUrl) return;
    try {
      await navigator.clipboard.writeText(installUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: select the input so the user can Cmd+C manually.
    }
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <Link to={`/fleet/${id}`} className="text-sm text-slate-500 hover:text-slate-700">← Back to tenant</Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Connect Slack</h1>
        <p className="mt-1 text-sm text-slate-600">
          Provisioning wizard · Step 3 of 5 — Slack
        </p>
      </div>

      {urlMutation.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {urlMutation.error.message}
        </div>
      )}

      {urlMutation.isPending && (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Generating install link…
        </div>
      )}

      {installUrl && !expired && (
        <div className="space-y-5">
          {/* Mode 1 — Authorize now (operator-driven) */}
          <section className="rounded-md border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-semibold text-slate-900">Authorize now</h2>
            <p className="mt-1 text-sm text-slate-600">
              Open Slack's authorize page in this tab. After approving, you'll be redirected
              back here automatically.
            </p>
            <a
              href={installUrl}
              className="mt-4 inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Authorize PowerMove in Slack →
            </a>
          </section>

          {/* Mode 2 — Copy link (delegated install + polling) */}
          <section className="rounded-md border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-semibold text-slate-900">Or send to the customer's admin</h2>
            <p className="mt-1 text-sm text-slate-600">
              Copy the link below and send it to whoever can authorize PowerMove in their Slack
              workspace. This page will detect when they finish and continue automatically.
            </p>
            <div className="mt-4 flex items-stretch gap-2">
              <input
                type="text"
                readOnly
                value={installUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-mono text-slate-700 shadow-sm"
              />
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Link expires in {expiresInMin}m {String(expiresInSec).padStart(2, '0')}s.
              Waiting for completion…
              {connectionQ.isFetching && <span className="ml-2 text-slate-400">(checking)</span>}
            </p>
          </section>
        </div>
      )}

      {expired && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p>The install link has expired.</p>
          <button
            type="button"
            onClick={() => urlMutation.mutate()}
            className="mt-3 rounded-md border border-amber-300 bg-white px-3 py-1 text-sm text-amber-900 hover:bg-amber-100"
          >
            Regenerate link
          </button>
        </div>
      )}
    </div>
  );
}
